import type { BatchHeader } from '../types/catalog-operations.types';
import type { DeliveryCompareEligibility } from '../types/delivery-compare.types';

/**
 * Whether a batch can open the compare view, and why not when it can't.
 *
 * Deliberately does NOT gate on `status === 'delivered'` — per
 * `docs/pipeline-stage-terminal-states.md`, a `COMPLETED` run with no push
 * evidence still folds to `delivered` at the top level, and the Delivered
 * pipeline stage renders `skipped` ("Not delivered") in that case. The stage
 * state is the real signal; `isDelivering`/`isRejected`/`isTrial` cover the
 * other terminal states a stale deep link could land on.
 *
 * Does NOT require `header.deliveryJobId` either, even though the compare
 * view uses it to scope the delivery lookup to one run — that field is
 * detail-only and commonly absent even on genuinely delivered batches (it's
 * populated for live-progress polling, not guaranteed for older/backfilled
 * rows). The lookup contract documents `deliveryJobId` as optional on the
 * request: omitting it just answers with each product's latest delivery
 * across every run instead of pinning to one. Blocking the whole feature on
 * its presence was a bug — a batch whose Delivered stage is complete has
 * already delivered, job id known or not.
 */
export function getDeliveryCompareEligibility(header: BatchHeader): DeliveryCompareEligibility {
  if (header.isTrial) return { eligible: false, reason: 'trial' };
  if (header.isRejected) return { eligible: false, reason: 'rejected' };
  if (header.isDelivering) return { eligible: false, reason: 'delivering' };
  const deliveredStage = header.stages.find((s) => s.key === 'delivered');
  if (deliveredStage?.state !== 'complete') return { eligible: false, reason: 'not_delivered' };
  return { eligible: true };
}
